import { useEffect, useMemo, useState } from 'react';
import { fetchBandsintownShows, mergeBandsintownShows } from '../data/bandsintown';
import { defaultWebsiteContent, type WebsiteContent, type WebsiteShow } from '../data/website';

/**
 * When auto-sync is on, the public site pulls tour dates straight from Bandsintown
 * on load. Published dates show until the fetch returns, and are kept as the
 * fallback if Bandsintown is unreachable.
 *
 * The fetched dates are held separately from the saved content and merged on
 * every render, so the page re-reading saved content — which it does constantly
 * to catch edits — can never discard dates that have already arrived.
 *
 * Saved content can also predate these settings, so blanks fall back to the
 * built-in values and auto-sync counts as on unless deliberately switched off.
 */
export function useLiveTourDates(content: WebsiteContent): WebsiteContent {
  const [liveShows, setLiveShows] = useState<WebsiteShow[]>([]);

  const artist = content.bandsintownArtist?.trim() || defaultWebsiteContent.bandsintownArtist;
  const appId = content.bandsintownAppId?.trim() || defaultWebsiteContent.bandsintownAppId;
  const autoSync = content.bandsintownAutoSync !== false;

  useEffect(() => {
    if (!autoSync || !artist || !appId) {
      setLiveShows([]);
      return undefined;
    }

    let cancelled = false;
    fetchBandsintownShows(artist, appId).then((result) => {
      if (cancelled || result.error || !result.shows.length) return;
      setLiveShows(result.shows);
    });

    return () => {
      cancelled = true;
    };
  }, [autoSync, artist, appId]);

  return useMemo(
    () => (liveShows.length ? { ...content, shows: mergeBandsintownShows(content.shows, liveShows) } : content),
    [content, liveShows],
  );
}
